import React from 'react'

import { useIsMobile } from '@/app/hooks/use-is-mobile'
import { Balance, Organization, SelectOption } from '@/app/types'

import CATIDesktopHeader from './cati-desktop-header'
import CATIMobileHeader from './cati-mobile-header'

type Props = {
  activeOrg?: Organization
  balance: Balance
  currentPage: string
  email: string
  token: string
  organizations?: SelectOption<number>[]

  logout: () => void
  switchOrganization: (args: {
    token: string
    item: {
      label: string
      value: number
    }
  }) => Promise<void>
}

const CATIHeader: React.FC<Props> = ({ organizations, token, logout, switchOrganization, ...props }) => {
  const isMobile = useIsMobile()

  const isAuthorized = !!token
  const hasOrg = isAuthorized && !!props.activeOrg

  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [panelExpanded, setPanelExpanded] = React.useState(false)

  const handleAccountMenuToggle = React.useCallback(() => {
    setAccountMenuOpen((s) => !s)
    setPanelExpanded(false)
  }, [])

  const handleMobileMenuToggle = React.useCallback(() => {
    setMobileMenuOpen((s) => !s)
  }, [])

  const handleOrganizationChange = React.useCallback(
    async ({ label, value }: SelectOption<number>) => {
      if (isMobile) {
        handleMobileMenuToggle()
      } else {
        handleAccountMenuToggle()
      }
      await switchOrganization({ token, item: { label, value } })
      // hacky. Need to wait on all other updates.
      setTimeout(() => {
        window.location.reload()
      }, 500)
    },
    [token, switchOrganization, handleAccountMenuToggle, handleMobileMenuToggle, isMobile],
  )

  const handlePanelToggle = React.useCallback(() => setPanelExpanded((s) => !s), [])

  if (isMobile) {
    return (
      <CATIMobileHeader
        activeOrg={props.activeOrg}
        email={props.email}
        organizations={organizations || []}
        isAuthorized={isAuthorized}
        hasOrg={hasOrg}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileNav={handleMobileMenuToggle}
        logout={logout}
        onOrgSelect={handleOrganizationChange}
      />
    )
  }

  return (
    <CATIDesktopHeader
      activeOrg={props.activeOrg}
      currentPage={props.currentPage}
      email={props.email}
      organizations={organizations || []}
      isAuthorized={isAuthorized}
      hasOrg={hasOrg}
      accountMenuOpen={accountMenuOpen}
      panelExpanded={panelExpanded}
      onAccountMenuToggle={handleAccountMenuToggle}
      onOrganizationChange={handleOrganizationChange}
      onPanelToggle={handlePanelToggle}
      logout={logout}
    />
  )
}

export default CATIHeader
